import React, { useMemo, useState } from "react";
import {
  Box,
  Flex,
  FormControl,
  FormLabel,
  Input,
  List,
  ListItem,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";

const normalizeValue = (value) => String(value || "").trim();
const toKey = (value) => normalizeValue(value).toLowerCase();

export default function CategoryMultiSelect({
  label,
  options,
  selected,
  onSelectedChange,
  inputValue,
  onInputChange,
  placeholder = "Start typing to filter categories",
}) {
  const [isOpen, setIsOpen] = useState(false);
  const listBg = useColorModeValue("white", "gray.700");
  const listBorder = useColorModeValue("gray.200", "gray.600");
  const listHover = useColorModeValue("gray.100", "gray.600");

  const selectedKeys = useMemo(
    () => new Set((selected || []).map((item) => toKey(item))),
    [selected]
  );

  const filteredOptions = useMemo(() => {
    const filterKey = toKey(inputValue);
    return (options || []).filter((option) => {
      const optionKey = toKey(option);
      if (selectedKeys.has(optionKey)) {
        return false;
      }
      if (!filterKey) {
        return true;
      }
      return optionKey.includes(filterKey);
    });
  }, [options, inputValue, selectedKeys]);

  const handleSelect = (value) => {
    if (!selectedKeys.has(toKey(value))) {
      onSelectedChange([...(selected || []), value]);
    }
    onInputChange("");
    setIsOpen(false);
  };

  const removeCategory = (categoryToRemove) => {
    onSelectedChange(
      (selected || []).filter((item) => toKey(item) !== toKey(categoryToRemove))
    );
  };

  return (
    <FormControl>
      {label ? <FormLabel>{label}</FormLabel> : null}
      <Box position="relative">
        <Input
          value={inputValue}
          onChange={(event) => {
            onInputChange(event.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          onBlur={() => {
            setTimeout(() => setIsOpen(false), 120);
          }}
          placeholder={placeholder}
        />
        {isOpen ? (
          <Box
            position="absolute"
            top="100%"
            left={0}
            right={0}
            mt="6px"
            borderWidth="1px"
            borderColor={listBorder}
            borderRadius="8px"
            bg={listBg}
            zIndex={10}
            maxH="200px"
            overflowY="auto"
          >
            {filteredOptions.length === 0 ? (
              <Text px="12px" py="8px" fontSize="sm" color="gray.500">
                No matches
              </Text>
            ) : (
              <List>
                {filteredOptions.map((option) => (
                  <ListItem
                    key={option}
                    px="12px"
                    py="8px"
                    cursor="pointer"
                    _hover={{ bg: listHover }}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => handleSelect(option)}
                  >
                    {option}
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        ) : null}
      </Box>
      <Flex wrap="wrap" mt={2}>
        {(selected || []).map((category) => (
          <Tag key={category} m={1} borderRadius="full">
            <TagLabel>{category}</TagLabel>
            <TagCloseButton onClick={() => removeCategory(category)} />
          </Tag>
        ))}
      </Flex>
    </FormControl>
  );
}
